/**
 * Which node next, and which verb on it.
 *
 * This file is the whole argument for an autonomous mode that can be trusted. It
 * is arithmetic over facts, with no model call and no composite score: which node
 * is least-worked, on the smallest branch, carrying an open contradiction has an
 * exact answer, and asking a model for it would replace a count with an opinion —
 * the same rule `panel.ts` states for counting agreement.
 *
 * TWO PROHIBITIONS govern every line below, and both are greppable:
 *
 *  - **Nothing here reads the model's self-ratings.** `novelty`, `confidence` and
 *    `tension` appear nowhere in this file. A loop that spent money on a model's
 *    rating of its own work would be following its own enthusiasm, and it would
 *    promote a number captioned "a sort order, not a probability" into a decision
 *    that costs money. `kindChannelOf` is used instead, so a contradiction counts
 *    the way it counts for every other consumer in the app.
 *  - **Nothing here can end a run.** This module ranks a candidate set; when the
 *    set is empty, that is a fact about the list, and the loop reports it as
 *    exactly that. There is no "good enough", no threshold, and no notion of a
 *    node worth stopping at.
 *
 * Pure: no store writes, no imports from `reason.ts`, no `Date.now()`. The loop
 * owns its mutable state and hands it in.
 */

import type {
  DeriveOp,
  MoveReason,
  ReasonNode,
  RunOpen,
  RunVerb,
  Session,
  Bridge,
} from '../core/types'
import { kindChannelOf, verificationLive, localEnds } from '../core/types'
import { childrenIndex, staleUnder } from '../core/store'
import { taintOf } from '../core/taint'
import { CONJECTURE_MAX_YIELD, anomalyRecords } from '../core/conjecture'
import { pendingEvidenceStaleOf } from '../core/rebase'

/**
 * How far from the seed a run works.
 *
 * Not a claim that ring seven is uninteresting — a claim about what one budget can
 * cover. Six rings of a branching graph is already more than a person reads in a
 * sitting, and a run that chased depth would spend everything down one line and
 * report a frontier it never looked at. Ends past this are counted and named in
 * the account, so the number is visible rather than merely enforced.
 */
export const MAX_DEPTH = 6

/** Moves against any one node in any one run. Three of the six ladder verbs, so a
 *  node gets worked from more than one angle and never becomes the whole run. The
 *  seed and the first ring take fewer — see `SEED_PER_NODE` and
 *  `FIRST_RING_PER_NODE`. */
export const PER_NODE = 3

/**
 * Moves `PER_NODE` is worth one angle over.
 *
 * Derived, not chosen: three angles against the middle budget row is one angle per
 * eight moves. It exists so that `perNode` can hold the same SHARE against a run a
 * spending ceiling has shortened, rather than handing a nine-move run the allowance
 * calibrated for twenty-four — which is a third of that run spent manufacturing
 * first-ring siblings the rest of it cannot develop.
 */
const MOVES_PER_ANGLE = 8

/** The lowest `perNode` may fall. Two is still "more than one angle", which is the
 *  whole reason the constant is three rather than one — a run that worked every node
 *  once would be a breadth-first sweep with no ladder in it at all. */
const MIN_PER_NODE = 2

/** The largest share of a budget any one first-ring branch may take. A pass adds
 *  three to five nodes to the branch it fires into, so without this the first
 *  branch to answer well would win the whole run by compounding. */
export const BRANCH_SHARE = 0.4

/**
 * The third angles a claim BELOW the first ring turns through, in the order the turn
 * takes them — `LADDER`'s own third to fifth rungs.
 *
 * With one fixed third rung every deeper claim a run reached took challenge →
 * deepen → experiment, so once a long run had swept the premises it never diverged
 * or inverted again. In the verb-mix simulation, which spends the live loop's budget,
 * a 48-move run whose seed took one move spent 14% of its reasoning on the two, all
 * of it on the first ring. Turned by arrival on the claim's ring, as a premise's
 * second angle is, the deeper claims a run reaches share the three between them, and
 * `deepen` keeps its second rung. Removing a claim shifts the turn of every claim
 * after it on its ring; nothing is bought twice, because spent angles are read off
 * `origin`.
 */
export const DEEPER_TURN: readonly RunVerb[] = ['experiment', 'invert', 'diverge']

/**
 * The order a run tries verbs on the FIRST claim to arrive on a ring below the
 * first; every later arrival on that ring takes the same order with `DEEPER_TURN`
 * rotated — see `deeperLadder`.
 *
 * `challenge` first is the tool's posture, and it is the one ordering decision
 * here that is an argument rather than an arithmetic: the first thing worth doing
 * to a fresh claim is looking for what breaks it, and a run that deepened first
 * would spend its budget elaborating claims nothing had attacked. `expand` is last
 * because its brief asks for four or five nodes and is the widest, most expensive
 * move — a run spends it only on a node with nothing else left to try.
 */
export const LADDER: readonly RunVerb[] = ['challenge', 'deepen', ...DEEPER_TURN, 'expand']

/** Every deeper order, one per turn, built once so a node's ladder is one stable
 *  array however many times a selection asks for it. The first is `LADDER` itself. */
const DEEPER_LADDERS: readonly (readonly RunVerb[])[] = DEEPER_TURN.map((_, k) =>
  k === 0
    ? LADDER
    : ['challenge', 'deepen', ...DEEPER_TURN.slice(k), ...DEEPER_TURN.slice(0, k), 'expand'],
)

/**
 * The order for the claim that arrived `arrival`th on its ring, below the first, 0
 * for the first: `challenge`, `deepen`, then `DEEPER_TURN` started at `arrival` mod
 * its length and wrapped. Arrival on the RING rather than among siblings, because a
 * run works each branch's first objection first, and every first objection is its
 * parent's first child: counted among siblings, every claim a run reached would take
 * the same turn. An arrival that is not a whole non-negative number takes the first.
 */
export function deeperLadder(arrival: number): readonly RunVerb[] {
  const k = Number.isInteger(arrival) && arrival > 0 ? arrival % DEEPER_TURN.length : 0
  return DEEPER_LADDERS[k]!
}

/**
 * The order a run tries verbs on the SEED: `LADDER` with `experiment` moved to the
 * front.
 *
 * The seed took `LADDER`'s first three angles, and on the live budget that was a
 * quarter of a twelve-move run spent on the question itself: two source checks and
 * the closing scan come out of the same twelve, so three seed moves left six for the
 * premises and the run reached three of them. The opening already carries the rival
 * view and the place the reasoning breaks (D129), so a first run's one move on the
 * question is the one the opening does not make: the cheapest test that could settle
 * it. The rest keep `LADDER`'s order, for the runs after.
 */
export const SEED_LADDER: readonly RunVerb[] = [
  'experiment',
  ...LADDER.filter((v) => v !== 'experiment'),
]

/**
 * Angles a run spends on the seed. One: the seed is always the first node a run
 * opens — the opening's own children mark it started — so whatever it is allowed
 * comes straight off the top of every budget, before any premise is reached. The
 * next run takes the seed's next rung.
 */
export const SEED_PER_NODE = 1

/**
 * The second angles a FIRST-RING node — a direct child of the seed — turns
 * through, in the order the turn takes them.
 *
 * The first ring is where the field is laid out: the opening's positions and
 * results, and whatever the run's own passes on the seed add beside them. `LADDER`
 * gave every one of those nodes challenge → deepen → experiment, so a run went three
 * rungs down each premise in turn and never widened the map — across 53 measured
 * run moves `diverge` ran once and `invert` five times, though those two were the
 * verbs that produced the most landscape when pressed by hand.
 *
 * One fixed second rung was the next mistake. With `diverge` second on every
 * premise and two angles per premise, a run spent diverge on every premise it
 * reached, `invert` — the third rung — never ran on a first pass over a map, and
 * `experiment` was left to the seed's own rung, so a 24-move run designed one test
 * where `LADDER` alone had designed about seven. Proposed tests are a section of
 * the report, and one people valued. So the second angle TURNS: the first premise
 * to arrive takes the first of these, the second the second, the fourth the first
 * again, and the three share the first ring between them.
 */
export const FIRST_RING_TURN: readonly RunVerb[] = ['diverge', 'invert', 'experiment']

/** Every first-ring order, one per turn, built once so a node's ladder is one stable
 *  array however many times a selection asks for it. Each is `challenge`, then the
 *  turn started at its own place and wrapped, then `deepen`, then `expand`. */
const FIRST_RING_LADDERS: readonly (readonly RunVerb[])[] = FIRST_RING_TURN.map((_, k) => [
  'challenge',
  ...FIRST_RING_TURN.slice(k),
  ...FIRST_RING_TURN.slice(0, k),
  'deepen',
  'expand',
])

/**
 * The order a run tries verbs on the FIRST premise to arrive; every later one takes
 * the same order with the turn rotated — see `firstRingLadder`.
 *
 * `challenge` first on every premise, for `LADDER`'s reason: what breaks a claim is
 * the first thing worth knowing about it. The turn next, because it is the whole
 * spread. `deepen` after the turn: every ring below already takes it second, so a
 * premise's own children get it, and a premise elaborated in place is not the map
 * widening. `expand` stays last, for `LADDER`'s reason, so every first-ring order is
 * `LADDER`'s six verbs in another order and the open-ends count cannot differ by
 * ring.
 */
export const FIRST_RING_LADDER: readonly RunVerb[] = FIRST_RING_LADDERS[0]!

/**
 * The order for the premise that arrived `arrival`th among the seed's children — its
 * arrival on the first ring — 0 for the first: `challenge`, then `FIRST_RING_TURN`
 * started at `arrival` mod its length and wrapped. So each premise still reaches all
 * three turn angles across later runs, each in its own rotation.
 *
 * Chosen by RING and ARRIVAL and by nothing else. The ring is a count of parent
 * links from the seed and the arrival is a place among the seed's children in
 * `order`, both stable across reloads, and no rating the model gave a node can move
 * either — so the turn a premise takes is a fact a reader can re-derive by counting
 * the premises above it. Deleting a premise shifts the turn of every one after it;
 * nothing is bought twice for that, because spent angles are read off `origin`, and
 * the next run simply takes the next unspent verb in the new order. An arrival that
 * is not a whole non-negative number takes the first turn.
 */
export function firstRingLadder(arrival: number): readonly RunVerb[] {
  const k = Number.isInteger(arrival) && arrival > 0 ? arrival % FIRST_RING_TURN.length : 0
  return FIRST_RING_LADDERS[k]!
}

/**
 * Angles a run spends on any one first-ring node before it moves on.
 *
 * Two, not `PER_NODE`'s three, and it is what makes the ladder above spread rather
 * than merely reorder: at three angles a twelve-move run works the seed and three
 * premises, at two it works the seed and four. And two is exactly the attack and one
 * turn, which is what lets the turn divide a run's premises three ways. It is a cap
 * for the RUN, like `PER_NODE`: the next run over the same map finds the attack and
 * the first turn already spent — read off the children's `origin` — and starts that
 * premise at its next turn.
 */
export const FIRST_RING_PER_NODE = 2

/**
 * The ladder by ring in one sentence, built from the constants above so none of them
 * can be edited without the sentence following it — the contract `cues.ts` keeps for
 * a cue's WHY line. A rule stated in prose beside the numbers it describes is the
 * copy that drifts first.
 */
export const LADDER_RULE =
  `the seed is worked with ${SEED_LADDER[0]} first, at most ${SEED_PER_NODE} angle in ` +
  `one run; a premise — a direct child of the seed — with ${FIRST_RING_LADDER[0]} and ` +
  `then one turn of ${FIRST_RING_TURN.join(', ')}, at most ${FIRST_RING_PER_NODE} ` +
  `angles; a deeper claim with ${LADDER[0]}, ${LADDER[1]} and then one turn of ` +
  `${DEEPER_TURN.join(', ')}, at most ${PER_NODE} angles; each turn taken in the ` +
  `node's order of arrival on its ring, wrapping`

/**
 * The most nodes each portfolio angle is allowed to return.
 *
 * These are the upper ends of the existing operation briefs, not observations or
 * expected yields. A packet reserves against the maximum because the graph-size
 * ceiling has to hold before the response exists. Keeping the table exhaustive
 * makes a new run verb stop the build until its capacity is decided explicitly.
 */
export const RUN_VERB_MAX_YIELD: Readonly<Record<RunVerb, number>> = {
  expand: 5,
  deepen: 4,
  challenge: 4,
  diverge: 4,
  experiment: 4,
  invert: 4,
}

/**
 * The most nodes ONE pass of this verb may put on the graph.
 *
 * The reserved table above covers the six run verbs. The other operations are
 * pressed by hand, so no packet reserves against them: `rebase` gets a hard five
 * matching its 3–5 brief, while `bridge`, `revise` and `outcome` use a loose six
 * that catches a runaway response without biting a conforming one.
 *
 * This has to be enforced on RECEIPT and cannot be pushed into the request.
 * Anthropic's structured outputs support array `minItems` only for 0 and 1 and
 * no `maxItems` at all, so the brief asks for a range in prose and the response
 * is free to overrun it — which one did, on the measured cohort. The brief is
 * the ask; this is the enforcement.
 *
 * `reason.ts` applies it as nodes arrive and the epistemics `node-count` gate
 * measures against it. ONE definition, deliberately: the gate exists to check the
 * app's rule, not to keep a second copy of it that can drift the way the two
 * `refuted` gates once did.
 */
export const maxYieldFor = (verb: DeriveOp): number =>
  reservedYieldFor(verb) ?? (verb === 'rebase' ? 5 : 6)

/**
 * The ceiling the app's admission path will ACTUALLY apply to a response, or
 * `undefined` where nothing applies one.
 *
 * A third question, and the three are not interchangeable. `reservedYieldFor` is
 * what a brief may state. `maxYieldFor` is what anything sizing a packet wants.
 * This one asks what would be DROPPED on arrival — and the answer differs by
 * verb, because not every verb is admitted by the same code. `runOp` applies
 * `maxYieldFor` inside its `onObject`, so everything it drives is bounded, the
 * rebase ceiling of five and the loose six for `revise` and `outcome` included.
 * `realizeBridge` has its own callback and applies no ceiling at all, so a bridge
 * is admitted whole.
 *
 * The epistemics `node-count` gate reads this one, because its claim is that a
 * response it calls out of bounds is one the app would not have admitted — and
 * that claim holds only where an admission ceiling exists to be exceeded.
 */
export const admissionCeilingFor = (verb: DeriveOp): number | undefined =>
  verb === 'bridge' ? undefined : maxYieldFor(verb)

/**
 * The reserved ceiling for a run verb, or `undefined` where there is no
 * reservation.
 *
 * Split out of `maxYieldFor` because two callers need the difference rather than
 * the number. `prompts.ts` states the ceiling to the model and must say nothing
 * to `bridge`, `revise`, `outcome` and `rebase`, whose own briefs state their
 * ranges — telling a bridge it may return six would widen the ask, not bind it.
 * Everything sizing a packet wants the fallback and calls `maxYieldFor`.
 *
 * `conjecture` is the one reservation outside the run verbs. It is not a
 * `RunVerb` — its schema is its own, so no packet can carry it — but its brief
 * states a ceiling and its admission enforces one, and both have to read the
 * same number. `CONJECTURE_MAX_YIELD` is that number.
 */
export const reservedYieldFor = (verb: DeriveOp): number | undefined =>
  Object.prototype.hasOwnProperty.call(RUN_VERB_MAX_YIELD, verb)
    ? RUN_VERB_MAX_YIELD[verb as RunVerb]
    : verb === 'conjecture'
      ? CONJECTURE_MAX_YIELD
      : undefined

/** Everything the loop owns and hands back in. Sets and Maps, which is why they
 *  live here and in the loop rather than in the store. */
export interface RunState {
  /** Verbs this run has spent per node, whatever they returned. A pass that came
   *  back empty was still paid for and must not be tried again. */
  attempted: Map<string, Set<DeriveOp>>
  /** Moves this run has made per node, capped by `perNode`. */
  moves: Map<string, number>
  /** Moves per first-ring branch, capped by the share. */
  branch: Map<string, number>
  /**
   * The ROOTS the user closed with "Not there" — never a materialised subtree.
   *
   * Snapshotting the descendants at click time looks equivalent and is not: the
   * pass being declined is still streaming, so its children land on the graph
   * afterwards, are not in the snapshot, and the run resumes on the branch that
   * was just refused. Ancestry is tested at use time instead, which is O(depth)
   * and cannot go stale.
   */
  closed: Set<string>
  /** Nodes this run threaded. A check lands on these or on the claims they stand
   *  on — never on a branch the run did not touch. See `checkableCandidates`. */
  produced: Set<string>
  /** The move budget, for the branch share. */
  budgetMoves: number

  /* ── steering, set once at the start and never written again ──────────────
     These three are the only fields here the loop does not mutate: they are what
     the user asked this run to do differently, and they live on `RunState` because
     it is already the thing the loop owns and hands in. Each is OPTIONAL and each
     absence means the module default, so a run that asked for nothing behaves
     exactly as it did before they existed.

     None of them is a judgement. A verb order is an order, a ring is a ring, and a
     subtree is a subtree — the two prohibitions in this file's header are about
     reading the model's ratings and about ending a run, and nothing below does
     either. */

  /**
   * The verb order for this run, replacing the seed's, every premise's and every
   * deeper claim's — an order somebody asked for is theirs everywhere.
   *
   * A shorter list is how `/only` works: give it one verb and the ladder for every
   * node is that verb and nothing else, so a node whose one verb is spent falls out
   * of the frontier rather than being worked from another angle. That is the point
   * of asking for one verb, and it means such a run ends on `frontier` far earlier
   * than its move budget — which the account already reports in those words.
   */
  ladder?: readonly DeriveOp[]
  /** The deepest ring this run works, replacing `MAX_DEPTH`. */
  maxDepth?: number
  /**
   * Restrict the whole run to this node and its descendants.
   *
   * The exact mirror of `closed`, and tested the same way for the same reason: a
   * node that did not exist when the user chose the focus is still inside it if its
   * ancestry says so, so this holds a ROOT and ancestry is walked at use time.
   */
  focus?: string
  /**
   * Restrict the run to several subtrees at once — a pursue run's cones.
   *
   * The two branches a candidate synthesis was fused from, plus the synthesis
   * itself, are three roots and `focus` holds one; a pursue run confined to the
   * first would never reach the second. Tested exactly as `focus` is, by walking
   * ancestry at use time, and for the same reason: a node that lands under one of
   * the roots after the run chose them is still inside it. When both are set a
   * node must satisfy both, which is the only reading under which each keeps its
   * own promise.
   */
  cones?: readonly string[]
  /**
   * Moves a spending ceiling is projected to actually buy, when it is smaller than
   * `budgetMoves`.
   *
   * The two caps below — the branch share and the per-node allowance — are shares of
   * a budget, and until this existed both were shares of the budget the user ASKED
   * for rather than of the one their ceiling pays for. A $6 ceiling against a
   * twenty-four-move request stops at about nine moves, and `max(3, ceil(24 × 0.4))`
   * is ten: the guard against one branch winning the whole run was arithmetically
   * incapable of firing for the entire run. `PER_NODE` compounded it from the other
   * side, spending a third of the real budget on the seed.
   *
   * Not a judgement and not a ceiling of its own — it cannot stop a run and it is
   * read by nothing but the two caps. It is arithmetic over list prices, computed
   * once in `startRun` where the prices already are, and ABSENT whenever the move
   * count is what binds. So a run with no ceiling, or with a generous one, is
   * byte-identical to the run before this field existed.
   */
  effectiveMoves?: number
}

/** What a run was asked to do differently. Every field optional; see `RunState`. */
export type RunSteering = Pick<
  RunState,
  'ladder' | 'maxDepth' | 'focus' | 'cones' | 'effectiveMoves'
>

export const freshRunState = (budgetMoves: number, steer: RunSteering = {}): RunState => ({
  attempted: new Map(),
  moves: new Map(),
  branch: new Map(),
  closed: new Set(),
  produced: new Set(),
  budgetMoves,
  // Spread rather than assigned field by field, so a steering field added to the
  // type reaches the state without this function needing to learn about it. Empty
  // and absent mean the same thing here, unlike everywhere in the codec.
  ...(steer.ladder && steer.ladder.length ? { ladder: steer.ladder } : {}),
  ...(steer.maxDepth !== undefined ? { maxDepth: steer.maxDepth } : {}),
  ...(steer.focus ? { focus: steer.focus } : {}),
  ...(steer.cones && steer.cones.length ? { cones: steer.cones } : {}),
  ...(steer.effectiveMoves !== undefined ? { effectiveMoves: steer.effectiveMoves } : {}),
})

/** The ring this run stops at. One reader, so a run that set no depth and a run
 *  that set the default depth are the same run. */
const depthCap = (st: RunState): number => st.maxDepth ?? MAX_DEPTH

/**
 * Whether a node is inside the focus, or true when there is none.
 *
 * Walks up like `underClosed`, and the guard against a cycle is there for the same
 * reason: a hand-edited document could describe one, and a policy that hung would
 * take the whole app with it.
 */
export function underFocus(s: Session, id: string, st: RunState): boolean {
  if (st.focus && !underAny(s, id, [st.focus])) return false
  if (st.cones && !underAny(s, id, st.cones)) return false
  return true
}

/**
 * The cones a pursue run works: the two branches the candidate was fused from,
 * and the synthesis itself. A foreign end has no branch on this canvas, so a
 * candidate with one contributes its local end and the synthesis alone. Here
 * beside the walk that reads them, so the plan pass and the loop build the same
 * list from the same rule.
 */
export function conesOf(b: Bridge, synthesisId: string): string[] {
  return [...new Set([...localEnds(b), synthesisId])]
}

/** Whether a node is at or under any of the roots. The one ancestry walk `focus`
 *  and `cones` share, guarded against a cycle for the reason `underFocus` gives.
 *  Exported for the plan pass, which confines its proposal to the same cones. */
export function underAny(s: Session, id: string, roots: readonly string[]): boolean {
  let cur: string | null = id
  const guard = new Set<string>()
  while (cur && !guard.has(cur)) {
    if (roots.includes(cur)) return true
    guard.add(cur)
    cur = s.nodes[cur]?.parentId ?? null
  }
  return false
}

/* ── structure, computed once per selection ───────────────────────────── */

interface Index {
  kids: Map<string, string[]>
  /** Insertion order → position. Arrival order is a fact and it is stable across
   *  reloads, which is what makes a run reproducible. */
  pos: Map<string, number>
  /** node id → the first-ring ancestor it hangs from, or its own id for a
   *  first-ring node. The root maps to itself. */
  branchOf: Map<string, string>
  /** Nodes beneath each first-ring branch, including it. */
  branchSize: Map<string, number>
  /** Nodes beneath each node, not including it. */
  subtree: Map<string, number>
  /** Ids with a collapsed node at or above them. */
  underFold: Set<string>
  /**
   * Parent links from the seed: the seed is 0, its children 1. Counted by the walk
   * rather than read off the stored `depth`, because this picks a ladder and the
   * walk is the structure itself — a node the walk never reaches has no ring and
   * keeps `LADDER`.
   */
  ring: Map<string, number>
  /** A node's position among the nodes on its own ring, in arrival order: 0 for the
   *  first. On the first ring that is its place among the seed's children. What
   *  `firstRingLadder` and `deeperLadder` turn on. */
  arrival: Map<string, number>
}

function indexOf(s: Session): Index {
  const kids = childrenIndex(s)
  const pos = new Map<string, number>()
  s.order.forEach((id, i) => pos.set(id, i))

  const branchOf = new Map<string, string>()
  const underFold = new Set<string>()
  const ring = new Map<string, number>()
  const rootId = s.order.find((id) => s.nodes[id]?.parentId === null)

  // One walk down from the root. `order` guarantees a parent precedes its
  // children, but a walk is what makes the fold and the branch inheritable
  // without re-climbing the lineage per node.
  if (rootId) {
    branchOf.set(rootId, rootId)
    ring.set(rootId, 0)
    const stack: string[] = [rootId]
    const seen = new Set<string>()
    while (stack.length) {
      const id = stack.pop()!
      if (seen.has(id)) continue
      seen.add(id)
      const n = s.nodes[id]
      if (!n) continue
      const foldedHere = underFold.has(id) || n.collapsed === true
      for (const c of kids.get(id) ?? []) {
        // A first-ring child starts its own branch; everything below inherits.
        branchOf.set(c, id === rootId ? c : (branchOf.get(id) ?? c))
        if (foldedHere) underFold.add(c)
        if (!ring.has(c)) ring.set(c, (ring.get(id) ?? 0) + 1)
        stack.push(c)
      }
    }
  }

  // Sizes, bottom-up over reverse insertion order — a child always precedes its
  // parent in the reverse walk, so one pass suffices.
  const subtree = new Map<string, number>()
  for (let i = s.order.length - 1; i >= 0; i--) {
    const id = s.order[i]!
    let n = 0
    for (const c of kids.get(id) ?? []) n += 1 + (subtree.get(c) ?? 0)
    subtree.set(id, n)
  }
  const branchSize = new Map<string, number>()
  for (const [id, b] of branchOf) {
    if (id === b) branchSize.set(b, 1 + (subtree.get(b) ?? 0))
  }

  // Counted over `order` against the ring the walk above assigned, so a node's turn
  // and its ring come from one structure and cannot disagree about who is first. A
  // node the walk never reached has no ring and no arrival.
  const arrival = new Map<string, number>()
  const onRing = new Map<number, number>()
  for (const id of s.order) {
    const r = ring.get(id)
    if (r === undefined) continue
    const k = onRing.get(r) ?? 0
    arrival.set(id, k)
    onRing.set(r, k + 1)
  }

  return { kids, pos, branchOf, branchSize, subtree, underFold, ring, arrival }
}

/** Whether a node is the seed, or hangs directly off it. The questions both ring
 *  rules — the ladder and the per-node allowance — ask, so they cannot disagree. */
const onSeed = (id: string, ix: Index): boolean => ix.ring.get(id) === 0
const onFirstRing = (id: string, ix: Index): boolean => ix.ring.get(id) === 1

/** The verb order for a node: what the run was steered onto, else its ring's — and
 *  below the seed, its arrival's turn. A node the walk never reached keeps `LADDER`. */
const ladderOf = (id: string, st: RunState, ix: Index): readonly DeriveOp[] => {
  if (st.ladder) return st.ladder
  if (onSeed(id, ix)) return SEED_LADDER
  const arrival = ix.arrival.get(id)
  if (arrival === undefined) return LADDER
  return onFirstRing(id, ix) ? firstRingLadder(arrival) : deeperLadder(arrival)
}

/** The verbs already spent on a node: what its children record as their origin,
 *  plus what this run has tried. Read from `origin` and never from `session.ops`,
 *  which is sliced to `MAX_OPS` on the way to disk — a long exploration would
 *  otherwise forget what it had already asked and ask it again.
 *
 *  Hand-written, so every verb a ladder can hold has to be named here: a verb
 *  missing from this list is one the policy re-issues forever. `conjecture` is on
 *  it since a run can be steered onto it (`STEERABLE`), and it counts as spent ON
 *  a conjecture from the start — a run never proposes conjectures under a
 *  conjecture, which would stack one proposal on another; its attack on a
 *  conjecture is the ladder's Challenge. */
function verbsSpent(s: Session, id: string, st: RunState, ix: Index): Set<DeriveOp> {
  const out = new Set<DeriveOp>(st.attempted.get(id) ?? [])
  if (s.nodes[id]?.kind === 'conjecture') out.add('conjecture')
  for (const c of ix.kids.get(id) ?? []) {
    const o = s.nodes[c]?.origin
    if (
      o === 'expand' ||
      o === 'deepen' ||
      o === 'challenge' ||
      o === 'diverge' ||
      o === 'experiment' ||
      o === 'invert' ||
      o === 'bridge' ||
      o === 'revise' ||
      o === 'outcome' ||
      o === 'conjecture'
    ) {
      out.add(o)
    }
  }
  return out
}

/** The next verb for a node, or null when this policy has spent them all. */
export function ladderFor(s: Session, id: string, st: RunState, ix?: Index): DeriveOp | null {
  const index = ix ?? indexOf(s)
  const spent = verbsSpent(s, id, st, index)
  return ladderOf(id, st, index).find((v) => !spent.has(v)) ?? null
}

/** Why a node is out of reach, or null when a run may still work it. The four
 *  values are the four the account breaks its open ends into. */
type OutOfReach = 'modelClosed' | 'pastDepth' | 'folded' | null

function outOfReach(n: ReasonNode, ix: Index, st: RunState): OutOfReach {
  // The model's own judgement that a path is exhausted closes an ITEM on the run's
  // list — never the run. A recorded result outranks it, exactly as `channelOf`
  // ranks them.
  if (n.terminal && !n.outcome) return 'modelClosed'
  if (n.depth > depthCap(st)) return 'pastDepth'
  if (n.collapsed === true || ix.underFold.has(n.id)) return 'folded'
  return null
}

/**
 * Whether this run may spend a move on a node.
 *
 * A gate, never a weight: everything here is a hard fact about the node or a
 * ceiling this run has already reached, and nothing is a judgement about whether
 * the node is worth working.
 */
function eligible(s: Session, n: ReasonNode, st: RunState, ix: Index): boolean {
  if (outOfReach(n, ix, st) !== null) return false
  if (underClosed(s, n.id, st)) return false
  if (!underFocus(s, n.id, st)) return false
  if ((st.moves.get(n.id) ?? 0) >= nodeAllowance(n.id, st, ix)) return false
  const b = ix.branchOf.get(n.id)
  if (b && (st.branch.get(b) ?? 0) >= branchCap(st)) return false
  return ladderFor(s, n.id, st, ix) !== null
}

/** The moves the two caps below are shares OF: what the ceiling is projected to buy
 *  when that is fewer than the run asked for, and the request otherwise. */
const capBase = (st: RunState): number =>
  st.effectiveMoves !== undefined && st.effectiveMoves < st.budgetMoves
    ? st.effectiveMoves
    : st.budgetMoves

/**
 * Angles this run will spend on any one node.
 *
 * `PER_NODE` against a full budget, and the same share against one a ceiling has
 * shortened — never more than `PER_NODE` and never fewer than `MIN_PER_NODE`, so a
 * generous ceiling cannot loosen the cap and a tight one cannot flatten the ladder
 * out of existence.
 */
export const perNode = (st: RunState): number => {
  const base = capBase(st)
  if (base >= st.budgetMoves) return PER_NODE
  return Math.max(MIN_PER_NODE, Math.min(PER_NODE, Math.round(base / MOVES_PER_ANGLE)))
}

/**
 * Angles this run will spend on THIS node: `perNode`, and no more than
 * `SEED_PER_NODE` on the seed or `FIRST_RING_PER_NODE` on the first ring. The smaller
 * of the two, so a ceiling that has already shortened the allowance is never widened
 * by the ring, and the gate that admits a move and the planner that sizes a packet
 * read one number.
 */
const nodeAllowance = (id: string, st: RunState, ix: Index): number =>
  onSeed(id, ix)
    ? Math.min(SEED_PER_NODE, perNode(st))
    : onFirstRing(id, ix)
      ? Math.min(FIRST_RING_PER_NODE, perNode(st))
      : perNode(st)

export const branchCap = (st: RunState): number =>
  Math.max(3, Math.ceil(capBase(st) * BRANCH_SHARE))

/** Whether a node is at or under a branch the user closed. Walks up, because the
 *  set holds roots: a node that did not exist when the user clicked is still
 *  under the root they clicked on. */
export function underClosed(s: Session, id: string, st: RunState): boolean {
  if (!st.closed.size) return false
  let cur: string | null = id
  const guard = new Set<string>()
  while (cur && !guard.has(cur)) {
    if (st.closed.has(cur)) return true
    guard.add(cur)
    cur = s.nodes[cur]?.parentId ?? null
  }
  return false
}

/**
 * How open a node is, as a small integer.
 *
 * The app's own already-written answer to "what is worth doing next": the reasoning
 * prompt names open contradictions and gaps explicitly, because they are the
 * fastest route to a useful next move. This reuses that judgement rather than
 * inventing a second one — and it reads `kindChannelOf`, so a high-tension node
 * counts as a contradiction the way it does everywhere else in the app.
 */
function openness(s: Session, n: ReasonNode): { score: number; why: MoveReason } {
  const ch = kindChannelOf(n)
  if (ch === 'tension') return { score: 3, why: 'contradiction' }
  if (ch === 'gap') return { score: 3, why: 'gap' }
  // Reasoned from something the record has since contradicted, and not
  // re-derived since. Scored with the contradictions rather than with the stale
  // nodes below, because the two are not the same finding: `stale` is a result
  // of any bearing landing above the reasoning, while this one contradicted it.
  // A run that keeps growing a subtree standing on a refutation is spending on
  // the least load-bearing part of the graph it could have picked.
  if (taintOf(s, n.id)) return { score: 3, why: 'foundation-broke' }
  if (verificationLive(n) && n.verification?.reading === 'refuted') {
    return { score: 2, why: 'disputed' }
  }
  // A node reasoned before a result was recorded above it describes a world that
  // has since been measured. Nothing else in the graph is more out of date.
  if (staleUnder(s, n.id)) return { score: 2, why: 'stale' }
  // Once the user has explicitly authored a new head, the retained before-side
  // remains visibly marked but stops competing for more automated run spend.
  if (pendingEvidenceStaleOf(s, n.id)) return { score: 2, why: 'stale' }
  if (verificationLive(n) && n.verification?.reading === 'mixed') {
    return { score: 1, why: 'disputed' }
  }
  return { score: 0, why: 'least-worked' }
}

export interface Move {
  nodeId: string
  label: string
  verb: DeriveOp
  why: MoveReason
}

/** One physical request's logical work. The selected move owns the display reason
 *  and label; the packet only needs the target and the exact ordered verbs. */
export interface PortfolioPlan {
  targetId: string
  verbs: RunVerb[]
}

/**
 * Hard headroom computed by the run loop immediately before it plans a packet.
 *
 * `logicalSlots` is already the minimum distance to every reserved boundary: a
 * grounding pass, the held closing scan, and the logical-move ceiling. Keeping
 * schedule arithmetic in the caller means this pure policy cannot accidentally
 * grow a second version of the cadence. `nodeCapacity` is the number of nodes
 * the run may still add under its own allowance — its headroom, not the graph's.
 */
export interface PortfolioLimits {
  logicalSlots: number
  nodeCapacity: number
}

const isRunVerb = (verb: DeriveOp): verb is RunVerb =>
  Object.prototype.hasOwnProperty.call(RUN_VERB_MAX_YIELD, verb)

/** Whole, non-negative capacity. Invalid values fail closed; positive infinity is
 *  useful in tests and for callers with no nearer boundary. */
const allowance = (n: number): number =>
  n === Number.POSITIVE_INFINITY ? n : Number.isFinite(n) ? Math.max(0, Math.floor(n)) : 0

/**
 * Pack the next two or three compatible angles over an already-selected target.
 *
 * This function does not select another target and cannot skip a ladder rung to
 * fill a packet. It re-applies every structural eligibility gate because the
 * graph or steering may have changed between selection and execution. Capacity
 * is reserved from the upper bound in each existing verb brief, so admitting one
 * packet cannot increase the graph-size overshoot of one ordinary pass.
 *
 * Pure policy only: graph structure, configured ladders, counters, and explicit
 * capacity. No authored explanation, label, evidence pointer, or score decides
 * whether a packet exists.
 */
export function planPortfolio(
  s: Session,
  st: RunState,
  move: Move,
  limits: PortfolioLimits,
): PortfolioPlan | null {
  const target = s.nodes[move.nodeId]
  if (!target || !isRunVerb(move.verb)) return null

  const ix = indexOf(s)
  if (!eligible(s, target, st, ix)) return null

  // Build the unspent prefix in active-ladder order. A non-packet derive operation
  // is a real boundary rather than something a packet may jump over. `seen` also
  // makes a malformed duplicate ladder harmless: one verb is never bought twice.
  const spent = verbsSpent(s, target.id, st, ix)
  const seen = new Set<DeriveOp>(spent)
  const remaining: RunVerb[] = []
  // The target's own ring's ladder, the one `chooseMove` picked from — a packet on a
  // premise packs the attack and that premise's own turn, never challenge and deepen.
  for (const verb of ladderOf(target.id, st, ix)) {
    if (seen.has(verb)) continue
    seen.add(verb)
    if (!isRunVerb(verb)) break
    remaining.push(verb)
  }
  if (remaining[0] !== move.verb) return null

  const nodeSlots = Math.max(0, nodeAllowance(target.id, st, ix) - (st.moves.get(target.id) ?? 0))
  const branch = target.parentId === null ? undefined : ix.branchOf.get(target.id)
  const branchSlots = branch
    ? Math.max(0, branchCap(st) - (st.branch.get(branch) ?? 0))
    : Number.POSITIVE_INFINITY
  const width = Math.min(
    3,
    remaining.length,
    nodeSlots,
    branchSlots,
    allowance(limits.logicalSlots),
  )
  if (width < 2) return null

  // Node headroom is not a count of angles: `expand` may return five nodes while
  // every other run verb may return four. Reserve the ordered prefix exactly.
  const capacity = allowance(limits.nodeCapacity)
  const verbs: RunVerb[] = []
  let reserved = 0
  for (let i = 0; i < width; i++) {
    const verb = remaining[i]!
    const next = reserved + RUN_VERB_MAX_YIELD[verb]
    if (next > capacity) break
    reserved = next
    verbs.push(verb)
  }

  return verbs.length >= 2 ? { targetId: target.id, verbs } : null
}

/**
 * Whether a node is one the run has already opened.
 *
 * The FIRST ordering key, and the one that stops this policy degenerating. Ranking
 * purely by least-worked looks right and is wrong in a way only a simulation
 * shows: every node a pass produces arrives with nothing run on it, so it enters
 * at the front of the queue, and the frontier grows faster than the ladder
 * advances. A 24-move run on a 28-node graph then spends all 24 moves on the first
 * rung — twenty-three challenges and nothing else — and no budget ever reaches
 * `deepen`, because there is always another untouched node.
 *
 * Finishing what it started fixes it and reads better besides: a node gets worked
 * from up to `perNode` angles — two on the first ring — before the run moves on,
 * which is what a person doing this by hand does.
 */
const started = (debt: number): number => (debt > 0 ? 0 : 1)

/**
 * The next move, or null when this run's list is empty.
 *
 * Four keys, lexicographic — already opened, then smallest branch, then most
 * open, then oldest — and the printed reason is the key that actually separated
 * the winner from the runner-up, so the sentence on screen is a comparison a
 * reader can re-run by hand rather than a description of one.
 *
 * Deliberately NOT a weighted sum. A composite score is unreadable (nobody can say
 * why 0.61 beat 0.58), it hides which input decided, and it is the exact shape
 * `types.ts` refuses for `measuredNovelty` so that one number cannot come to mean
 * two things.
 */
export function chooseMove(s: Session, st: RunState): Move | null {
  const ix = indexOf(s)
  const cand: Array<{
    n: ReasonNode
    /** 0 = already opened by someone, 1 = fresh. Ascending. */
    open0: number
    load: number
    open: number
    why: MoveReason
    pos: number
  }> = []

  for (const id of s.order) {
    const n = s.nodes[id]
    if (!n) continue
    if (!eligible(s, n, st, ix)) continue
    const o = openness(s, n)
    cand.push({
      n,
      open0: started(verbsSpent(s, id, st, ix).size),
      load: ix.branchSize.get(ix.branchOf.get(id) ?? id) ?? 0,
      open: o.score,
      why: o.why,
      pos: ix.pos.get(id) ?? 0,
    })
  }
  if (!cand.length) return null

  cand.sort((a, b) => a.open0 - b.open0 || a.load - b.load || b.open - a.open || a.pos - b.pos)
  const win = cand[0]!
  const next = cand[1]
  const verb = ladderFor(s, win.n.id, st, ix)
  if (!verb) return null

  // The deciding key, in the same order the sort used them.
  let why: MoveReason
  if (!next) why = 'only-one'
  else if (win.open0 !== next.open0) why = 'in-progress'
  else if (win.load !== next.load) why = 'smallest-branch'
  else if (win.open !== next.open) why = win.why
  else why = 'oldest'

  return { nodeId: win.n.id, label: win.n.label, verb, why }
}

/**
 * Whether a claim may be one a run spends a source check on.
 *
 * This used to also refuse a gap and any claim with neither a digit nor an evidence
 * pointer, on the theory that those come back `not-checkable` and waste a live web
 * pass. Twenty real checks measured both halves of that wrong (D364): all eight such
 * claims came back with a reading, while the claims the digit admitted included
 * this graph's own simulation results, which no source states. And a wasted check is
 * cheap — the check model declines without searching, or reads `not-checkable` after
 * a few — so the filter was paying for a small saving with the checks worth making.
 * A run gets two to four checks; which claims get them is `rankGrounding`'s job, and
 * the judge's rating sits there as its last key (D106).
 */
export function checkableClaim(n: ReasonNode): boolean {
  if (n.parentId === null) return false
  if (n.verification) return false
  // A proposed test, not a statement about the world. The one experiment the
  // measurement checked read `not-checkable`.
  if (n.kind === 'experiment') return false
  // A conjecture's search is the prior-art search, which a run queues when one
  // lands. A source check on a claim no source the model knows states reads
  // "nothing found" — the one reading that tells nobody anything, bought with a
  // live web pass.
  if (n.kind === 'conjecture') return false
  return true
}

/**
 * The claim a run may spend a check on, and the key that chose it.
 *
 * Ranked by how much of the graph rests on the node — the size of the subtree
 * beneath it — and NOT by novelty, confidence or anything the model rated. That
 * ranking is the one that cannot be read as a hunt for the interesting result: it
 * checks the claims the rest of the argument stands on, which is where being wrong
 * costs the most.
 *
 * The reason is returned rather than assumed, because early in a run every
 * candidate has an empty subtree and NOTHING rests on any of them — asserting
 * "more of the graph rests on it than on anything else checkable" there would be a
 * sentence the comparison did not earn. When no subtree separates the winner from
 * the runner-up, the honest key is the one that actually did.
 *
 * `openness` sits between the two for exactly that case, and it is not a refinement
 * — in a wide, shallow graph EVERY candidate has an empty subtree for the whole run,
 * so before it was here every check a run ever spent was chosen by arrival order
 * alone. Arrival order systematically picks the most conventional claims, because a
 * model states the well-known thing first; the sharpest claim in a graph arrives late
 * with nothing beneath it and was never a candidate. This is the same ordering
 * `chooseMove` already uses and the app's own answer to what deserves attention, so
 * the two passes cannot disagree about it — and it reads `kindChannelOf`, taint and
 * verification state only, which is why the header's two prohibitions still hold.
 *
 * Restricted to what this run produced and the claims that output stands on — see
 * `checkableCandidates` — and gated exactly as `eligible` gates a reasoning move: a
 * branch the user closed with "Not there" or folded away must not be where the run
 * spends one of its two-to-four live web passes — and the subtree ranking would
 * otherwise make a closed branch the LIKELIEST target, since it is usually the one
 * the run has built out most.
 */
export function verifyCandidates(
  s: Session,
  st: RunState,
  /**
   * The judge's probability that a search could settle each candidate, by node
   * id, when a judge was asked. The LAST key before arrival order, and never a
   * filter: a candidate absent from the map is still a candidate and sorts as
   * though the judge had answered no. See `rankGrounding` and D106.
   */
  checkability?: ReadonlyMap<string, number>,
): { node: ReasonNode; why: MoveReason } | null {
  return rankGrounding(s, checkableCandidates(s, st), indexOf(s), checkability)
}

/**
 * Every claim a run could spend a check on, unranked: the structural admission,
 * exactly as `verifyCandidates` applies it.
 *
 * Exported because the loop asks the judge about this set before it ranks it,
 * and the set the judge rates has to be the set the ranking sees. A second copy
 * of the predicate chain would let the judge rate a claim the ranking would then
 * refuse, or miss one it would take.
 *
 * The set is what this run produced AND every claim above it short of the seed.
 * Output alone was the rule until a study run checked two speculative leaves while
 * the opening premises — the claims carrying the largest subtrees on the board —
 * were never candidates, because the run had not written them. The run reasoned
 * FROM them, so a check on one is a check on this run's footing, and the subtree
 * ranking then puts the premise ahead of anything built on it, which is the point.
 * A branch the run never touched is still never a candidate, and the seed is the
 * question rather than a claim (`checkableClaim` refuses it, and the climb stops
 * there anyway). Emitted in arrival order, so the judge's batches and the ranking's
 * last key see one stable order however the run happened to reach each claim.
 */
export function checkableCandidates(s: Session, st: RunState): ReasonNode[] {
  const ix = indexOf(s)
  const reached = new Set<string>()
  for (const id of st.produced) {
    // Up the primary lineage to the seed. `reached` doubles as the cycle guard and
    // stops a climb where an earlier one already went, so the walk is linear in
    // the claims it touches rather than in produced × depth.
    let cur: string | null = id
    while (cur && !reached.has(cur)) {
      const n: ReasonNode | undefined = s.nodes[cur]
      if (!n || n.parentId === null) break
      reached.add(cur)
      cur = n.parentId
    }
  }
  const out: ReasonNode[] = []
  for (const id of s.order) {
    if (!reached.has(id)) continue
    const n = s.nodes[id]
    if (!n || !checkableClaim(n)) continue
    if (underClosed(s, id, st)) continue
    if (!underFocus(s, id, st)) continue
    if (outOfReach(n, ix, st) !== null) continue
    out.push(n)
  }
  return out
}

/**
 * The claim a run may spend a panel on, and the key that chose it.
 *
 * The same arithmetic as `verifyCandidates` over a different candidate set, and the
 * sameness is deliberate: both passes examine a claim the graph already holds rather
 * than growing a new one, so the honest question for both is which claim the rest of
 * the argument most rests on. Sharing the ranking is what lets them share
 * `load-bearing`, and a second ranking for the same question would be a second
 * answer to it. Literally shared now, through `rankGrounding` — two copies of one
 * sort is how the sameness the paragraph above asserts quietly stops being true.
 *
 * There is no `checkableClaim` equivalent here, and the absence is the point. A
 * search needs a statement for a source to bear on, which is why that predicate
 * refuses experiments and conjectures; a panel rules on whether a claim FOLLOWS FROM
 * ITS PARENT, and every derived node is that. A gap is a legitimate target —
 * `undecidable` is one of the four verdicts precisely for a claim that turns on a
 * quantity nobody has.
 *
 * Gated exactly as `verifyCandidates` is gated, for the same reasons: a branch the
 * user closed with "Not there" or folded away must not be where three passes go, and
 * the subtree ranking would otherwise make a closed branch the LIKELIEST target
 * since it is usually the one the run has built out most.
 */
export function panelCandidate(
  s: Session,
  st: RunState,
): { node: ReasonNode; why: MoveReason } | null {
  const ix = indexOf(s)
  const out: ReasonNode[] = []
  for (const id of st.produced) {
    const n = s.nodes[id]
    if (!n || n.parentId === null) continue
    // A node already ruled on. `runPanel` would overwrite the record rather than
    // refuse, so this is the only thing stopping a run spending nine passes ruling
    // three times on whichever claim has the largest subtree.
    if (n.panel) continue
    if (underClosed(s, id, st)) continue
    if (!underFocus(s, id, st)) continue
    if (outOfReach(n, ix, st) !== null) continue
    out.push(n)
  }
  return rankGrounding(s, out, ix)
}

/**
 * The ranking the source check and the panel share, over their own candidate sets.
 *
 * ONE function rather than two identical sorts, which is what the comments on both
 * callers already said was deliberate. Three keys, lexicographic — what rests on it,
 * then how open it is, then arrival order — and the returned reason is the key that
 * actually separated the winner from the runner-up, exactly as `chooseMove` reports
 * its own.
 *
 * `openness` is precomputed per candidate rather than called from the comparator:
 * `taintOf` and `staleUnder` each walk the graph, and a sort would call them O(n log n)
 * times for an answer that cannot change during it.
 */
function rankGrounding(
  s: Session,
  cand: ReasonNode[],
  ix: Index,
  /**
   * A fourth key, between openness and arrival order, and only for the source
   * check — the panel passes nothing here. Where it applies it replaces the one
   * key the docstring above already calls the weakest: arrival order
   * "systematically picks the most conventional claims". A judge's probability
   * that a search could settle the claim is a better tie-break than the order
   * the model happened to state things in, and it is still only a tie-break:
   * what rests on a claim and how open it is decide first, exactly as before,
   * and the header's prohibitions hold — this number is not a self-rating, and
   * nothing here can end a run. A candidate the map does not name sorts as a
   * zero, never out of the list.
   */
  checkability?: ReadonlyMap<string, number>,
): { node: ReasonNode; why: MoveReason } | null {
  if (!cand.length) return null
  const open = new Map<string, { score: number; why: MoveReason }>()
  for (const n of cand) open.set(n.id, openness(s, n))
  const judged = (id: string): number => checkability?.get(id) ?? 0
  cand.sort(
    (a, b) =>
      (ix.subtree.get(b.id) ?? 0) - (ix.subtree.get(a.id) ?? 0) ||
      (open.get(b.id)?.score ?? 0) - (open.get(a.id)?.score ?? 0) ||
      (checkability ? judged(b.id) - judged(a.id) : 0) ||
      (ix.pos.get(a.id) ?? 0) - (ix.pos.get(b.id) ?? 0),
  )
  const win = cand[0]!
  const next = cand[1]
  // Unchanged from before openness was a key: with nothing beneath the runner-up — or
  // no runner-up at all — a non-empty subtree is still what separated them.
  const rests = (ix.subtree.get(win.id) ?? 0) > (next ? (ix.subtree.get(next.id) ?? 0) : 0)
  if (rests) return { node: win, why: 'load-bearing' }
  const wo = open.get(win.id)!
  const no = next ? (open.get(next.id)?.score ?? 0) : 0
  if (wo.score > no) return { node: win, why: wo.why }
  // The reason is the key that DECIDED, as everywhere: `checkable` is claimed
  // only when the judge's number separated the winner from the runner-up, and a
  // lone candidate or a tie on it falls through to arrival order.
  if (checkability && next && judged(win.id) > judged(next.id)) {
    return { node: win, why: 'checkable' }
  }
  return { node: win, why: 'oldest' }
}

/**
 * The branch a run may hand to a model from the other house, and the key that chose
 * it.
 *
 * Ranked by COVERAGE and not by what rests on it, which is the one place this file
 * departs from the ranking the two passes above share — and it has to. One audit
 * examines a whole branch at once, up to `AUDIT_MAX_CLAIMS` of it, so the question
 * is not "which claim matters most" but "which branch has the most claims nobody
 * from another house has looked at yet". Rank a second audit by subtree size and it
 * picks the branch the first one just finished, every time.
 *
 * The count is bottom-up in one pass rather than a walk per candidate: a graph has
 * no ceiling of its own (D74), so a per-candidate walk is a walk over a graph of
 * any size, and the count is also what the SECOND audit needs in order to see that
 * the first one covered the subtree it would otherwise pick again — so it has to be
 * exact rather than approximate, and one pass is both.
 *
 * Nodes carrying an audit already are not counted, which is what makes this drain:
 * once every claim under a branch has been examined the branch scores zero and drops
 * out, and when every branch has, this returns null and the loop reports a fact
 * about its list — never a judgement that the graph is sound.
 */
export function auditCandidate(
  s: Session,
  st: RunState,
): { node: ReasonNode; why: MoveReason } | null {
  const ix = indexOf(s)
  // Claims at or beneath each node that no other model has examined. Reverse arrival
  // order, so a child is always counted before its parent and one pass suffices —
  // the same walk `indexOf` uses for `subtree`.
  const fresh = new Map<string, number>()
  for (let i = s.order.length - 1; i >= 0; i--) {
    const id = s.order[i]!
    const n = s.nodes[id]
    if (!n) continue
    // The seed is not a claim anyone wrote, so it is never part of the count — the
    // same exclusion `auditTargets` makes when it collects the branch to hand over.
    let c = n.parentId !== null && !n.audit ? 1 : 0
    for (const k of ix.kids.get(id) ?? []) c += fresh.get(k) ?? 0
    fresh.set(id, c)
  }

  const out: ReasonNode[] = []
  for (const id of st.produced) {
    const n = s.nodes[id]
    if (!n || n.parentId === null) continue
    if ((fresh.get(id) ?? 0) < 1) continue
    if (underClosed(s, id, st)) continue
    if (!underFocus(s, id, st)) continue
    if (outOfReach(n, ix, st) !== null) continue
    out.push(n)
  }
  out.sort(
    (a, b) =>
      (fresh.get(b.id) ?? 0) - (fresh.get(a.id) ?? 0) ||
      (ix.pos.get(a.id) ?? 0) - (ix.pos.get(b.id) ?? 0),
  )
  const win = out[0]
  if (!win) return null
  const next = out[1]
  const most = (fresh.get(win.id) ?? 0) > (next ? (fresh.get(next.id) ?? 0) : 0)
  return { node: win, why: most ? 'unexamined' : 'oldest' }
}

/**
 * The node a run may spend a conjecture pass on, and the key that chose it.
 *
 * A conjecture pass starts from what does not fit, so a run aims it where the
 * graph's records of what does not fit concentrate: the node whose subtree
 * carries the most of the rows `anomalyRecords` builds the pass's own WHAT DOES
 * NOT FIT block from — refuted results and sources, mixed readings, split panels,
 * audit objections, open contradictions and gaps — counting only the records no
 * conjecture hangs beneath yet. By PARENTAGE, and never by the anchors a
 * conjecture names: anchors are model-written, and a run whose spending followed
 * them would be steered by model output, which `planPortfolio`'s contract and
 * D28 both refuse.
 *
 * It DRAINS, the way `auditCandidate` does. A node with a conjecture anywhere
 * beneath it is out, and so is one this run has already spent a conjecture pass
 * on — the pass that came back with nothing was still paid for. So every pass
 * retires its target, and when nothing is left the gate yields to the ladder: a
 * fact about the list, never a judgement that the graph is explained.
 *
 * A conjecture is never a target: a run does not stack one proposal on another.
 * Otherwise gated exactly as the grounding passes are — a branch the user closed,
 * folded, or left outside the focus is not where a paid pass goes. Not limited to
 * what this run produced, unlike the checks: a conjecture pass reasons rather
 * than grounds, and the records it starts from are often the person's own.
 *
 * Ties by arrival order. No judge orders them: invariant 10 would allow one to
 * order ties only, and none has been designed or measured for this.
 */
export function conjectureCandidate(
  s: Session,
  st: RunState,
): { node: ReasonNode; why: MoveReason } | null {
  const ix = indexOf(s)
  // Whether a conjecture hangs anywhere beneath each node, and the records at or
  // beneath it — both bottom-up in one pass over reverse arrival order, the walk
  // `auditCandidate` makes, so a child is always counted first.
  const own = new Map<string, number>()
  for (const r of anomalyRecords(s)) own.set(r.nodeId, (own.get(r.nodeId) ?? 0) + 1)
  const beneath = new Set<string>()
  const unexplained = new Map<string, number>()
  for (let i = s.order.length - 1; i >= 0; i--) {
    const id = s.order[i]!
    if (!s.nodes[id]) continue
    let under = false
    let count = 0
    for (const k of ix.kids.get(id) ?? []) {
      if (s.nodes[k]?.kind === 'conjecture' || beneath.has(k)) under = true
      count += unexplained.get(k) ?? 0
    }
    if (under) beneath.add(id)
    // Every record counts here, because only a node with no conjecture beneath it
    // is a candidate — and none of the records under such a node can have one.
    unexplained.set(id, count + (own.get(id) ?? 0))
  }

  const out: ReasonNode[] = []
  for (const id of s.order) {
    const n = s.nodes[id]
    if (!n || n.kind === 'conjecture') continue
    if ((unexplained.get(id) ?? 0) < 1) continue
    if (beneath.has(id)) continue
    if (st.attempted.get(id)?.has('conjecture')) continue
    if (underClosed(s, id, st)) continue
    if (!underFocus(s, id, st)) continue
    if (outOfReach(n, ix, st) !== null) continue
    out.push(n)
  }
  out.sort(
    (a, b) =>
      (unexplained.get(b.id) ?? 0) - (unexplained.get(a.id) ?? 0) ||
      (ix.pos.get(a.id) ?? 0) - (ix.pos.get(b.id) ?? 0),
  )
  const win = out[0]
  if (!win) return null
  const next = out[1]
  const most = (unexplained.get(win.id) ?? 0) > (next ? (unexplained.get(next.id) ?? 0) : 0)
  return { node: win, why: most ? 'unexplained' : 'oldest' }
}

/**
 * The claims a source check said held up, and how much of the graph rests on them.
 *
 * USER-INVOKED, and it must stay that way. Nothing in the run loop calls this and
 * nothing should: a run that went looking for consensus to attack would be spending
 * somebody's money on a hunt the policy above is deliberately incapable of — see the
 * header's second prohibition. This answers a question a person asks by pressing a
 * button, and the pass it leads to is one press and one pass.
 *
 * The reason it exists is that a `supported` reading currently retires a claim from
 * every surface at once. `openness` scores it 0, `checkableClaim` refuses anything
 * already carrying a verification, and so a claim with a large subtree beneath it and
 * agreement above it becomes the one place in the graph nothing is looking at — which is
 * exactly where a hidden assumption survives. The README already makes the argument and
 * then does not act on it: *"A reading is what a search returned on a date. Not what is
 * true"*, and *"sources appear to agree means the results that came back read as
 * consistent with the claim — a record, not a proof."*
 *
 * It is a `challenge`, not a re-check. So `checkableClaim` needs no loosening, the
 * citation gate is untouched, and the pass costs no live web requests.
 *
 * It DRAINS, the way `auditCandidate` drains: a claim whose check has been challenged
 * since leaves the list, so pressing the button repeatedly works down the list instead
 * of attacking the heaviest claim forever.
 */
export interface Contest {
  /** Heaviest first, then arrival order — the ranking the grounding passes share. */
  targets: ReasonNode[]
  /** Claims resting on them, as a UNION. Summing subtrees would count a claim under
   *  two supported ancestors twice and could exceed the graph. */
  resting: number
}

export function contestable(s: Session): Contest {
  const ix = indexOf(s)
  const targets: ReasonNode[] = []
  for (const id of s.order) {
    const n = s.nodes[id]
    if (!n || n.parentId === null) continue
    if (!verificationLive(n) || n.verification?.reading !== 'supported') continue
    // A reasoned dead end is a different retirement, and re-opening it is what
    // relaxing its reason is for. A recorded result outranks the model's judgement
    // that a path is exhausted, exactly as `outOfReach` ranks the two.
    if (n.terminal && !n.outcome) continue
    if (n.collapsed === true || ix.underFold.has(id)) continue
    // Already contested since the check. Read off `origin` and a timestamp, never off
    // `session.ops`, which is sliced on the way to disk.
    const at = n.verification.checkedAt
    const done = (ix.kids.get(id) ?? []).some((c) => {
      const k = s.nodes[c]
      return k?.origin === 'challenge' && k.createdAt >= at
    })
    if (done) continue
    targets.push(n)
  }
  targets.sort(
    (a, b) =>
      (ix.subtree.get(b.id) ?? 0) - (ix.subtree.get(a.id) ?? 0) ||
      (ix.pos.get(a.id) ?? 0) - (ix.pos.get(b.id) ?? 0),
  )

  const under = new Set<string>()
  for (const t of targets) {
    const stack = [...(ix.kids.get(t.id) ?? [])]
    while (stack.length) {
      const id = stack.pop()!
      if (under.has(id)) continue
      under.add(id)
      for (const c of ix.kids.get(id) ?? []) stack.push(c)
    }
  }
  // A target that is itself under another target is not something "resting on" the set.
  for (const t of targets) under.delete(t.id)
  return { targets, resting: under.size }
}

/**
 * Whether a claim can be one of the few a checks-only run is pressed for.
 *
 * The run's own reach first — `outOfReach`, "Not there", the focus — so a claim the
 * press names is a claim the loop will still take when its turn comes. Then the
 * claim: never the seed, which is the question; never an experiment, which is a
 * proposed test rather than a statement and comes back `not-checkable`; never one
 * whose source check still describes its words, since checking it again buys the
 * same reading; and never a leaf, because nothing rests on it. A gap is a candidate:
 * the ones measured were statements about the record — a replication that failed,
 * a method nobody has — and every one came back with a reading (D364).
 */
function restingEligible(s: Session, n: ReasonNode, st: RunState, ix: Index): boolean {
  if (n.parentId === null) return false
  if (n.kind === 'experiment') return false
  if (verificationLive(n)) return false
  if ((ix.subtree.get(n.id) ?? 0) < 1) return false
  if (outOfReach(n, ix, st) !== null) return false
  if (underClosed(s, n.id, st)) return false
  return underFocus(s, n.id, st)
}

/** Most beneath first, then arrival: the report's own order for what the rest of the
 *  argument is resting on, and no other key — no openness, no judge, no rating. */
const heaviestFirst =
  (ix: Index) =>
  (a: ReasonNode, b: ReasonNode): number =>
    (ix.subtree.get(b.id) ?? 0) - (ix.subtree.get(a.id) ?? 0) ||
    (ix.pos.get(a.id) ?? 0) - (ix.pos.get(b.id) ?? 0)

/**
 * The claims most of the graph rests on that no live check has touched, heaviest
 * first, at most `n` of them.
 *
 * USER-INVOKED, like `contestable`: the thin cue's press asks this, names what comes
 * back, and hands the ids to a checks-only run (`RunSpec.checkTargets`). Nothing in
 * the loop calls it to find work. Ranked by how many claims sit beneath each one and
 * nothing else — the report's "resting on" order — so which claims the press names is
 * a count a reader can redo by hand, and a self-rating has no way in.
 *
 * `st` is the run state the answer must hold under; a press has none yet, and a fresh
 * one applies only the reach every run applies.
 */
export function restingTargets(
  s: Session,
  n: number,
  st: RunState = freshRunState(Math.max(1, n)),
): { node: ReasonNode; below: number }[] {
  const ix = indexOf(s)
  const out: ReasonNode[] = []
  for (const id of s.order) {
    const node = s.nodes[id]
    if (node && restingEligible(s, node, st, ix)) out.push(node)
  }
  // `below` rides out with each claim because it is the reason the claim is on the
  // list, and the press prints it — a second count taken by the caller could differ.
  return out
    .sort(heaviestFirst(ix))
    .slice(0, Math.max(0, Math.trunc(n)))
    .map((node) => ({ node, below: ix.subtree.get(node.id) ?? 0 }))
}

/**
 * The next of a checks-only run's named claims, and the key that put it first — or
 * null when none of them is left to check.
 *
 * Only ever one of `listed`: the press named them, and a run that wandered to a
 * fourth claim when one of the three dropped out would be spending on a claim nobody
 * was shown. `done` is what this run already checked, whatever the check returned —
 * a pass that failed was still paid for. Each remaining claim is re-gated as the
 * graph now stands, so one folded, closed with "Not there", pruned or checked by
 * hand in a gap drops out rather than being bought anyway, and the rest are re-ranked
 * by the same count, so the reason printed is the comparison that actually decided:
 * `load-bearing` when more rests on it than on the next one, arrival order on a tie.
 */
export function nextRestingCheck(
  s: Session,
  st: RunState,
  listed: readonly string[],
  done: ReadonlySet<string>,
): { node: ReasonNode; why: MoveReason } | null {
  const ix = indexOf(s)
  const left: ReasonNode[] = []
  for (const id of listed) {
    const node = s.nodes[id]
    if (!node || done.has(id) || left.includes(node)) continue
    if (restingEligible(s, node, st, ix)) left.push(node)
  }
  left.sort(heaviestFirst(ix))
  const win = left[0]
  if (!win) return null
  const next = left[1]
  const rests = (ix.subtree.get(win.id) ?? 0) > (next ? (ix.subtree.get(next.id) ?? 0) : 0)
  return { node: win, why: rests ? 'load-bearing' : 'oldest' }
}

/**
 * What is still open, broken out by why it is out of reach.
 *
 * Four disjoint counts over every non-root node with an unspent verb. A single
 * number would under-report exactly where it matters: a run that hits the depth
 * ceiling leaves a whole ring untouched, and folding that into "reachable" would
 * make the one figure built to prevent an impression of completeness the figure
 * that creates it.
 */
export function countOpen(s: Session, st: RunState): RunOpen {
  const ix = indexOf(s)
  const out: RunOpen = { reachable: 0, pastDepth: 0, folded: 0, modelClosed: 0 }
  for (const id of s.order) {
    const n = s.nodes[id]
    if (!n || n.parentId === null) continue
    // `st` so `pastDepth` is measured against the ring THIS run works to. A run
    // given a deeper ceiling really does have fewer ends past it, and a bucket that
    // ignored that would print the default's figure over a run that never used it.
    const reach = outOfReach(n, ix, st)
    if (reach !== null) {
      out[reach]++
      continue
    }
    // Structurally reachable, and this policy has a verb left for it. The per-run
    // caps are deliberately NOT applied here: this figure answers "where could a
    // run still go", not "what did this run's bookkeeping allow".
    //
    // A focus is not applied either, and it is the one case worth naming: it IS this
    // run's bookkeeping rather than a fact about the graph, so an end outside the
    // focus is still somewhere a run could go. The account says what the run was
    // restricted to instead, which is the honest way round — narrowing this figure
    // would make a focused run look as though the graph had fewer open ends than it
    // has.
    if (ladderFor(s, id, st, ix) !== null) out.reachable++
  }
  return out
}
